// frontend/src/pages/ManualOrder.js
import React, { useState, useEffect, useRef } from 'react';
import { api, channelLabel } from '../App';

const CHANNELS = ['poshmark', 'mercari', 'depop', 'vinted', 'facebook', 'etsy', 'other'];

export default function ManualOrder() {
  const [search,      setSearch]      = useState('');
  const [results,     setResults]     = useState([]);
  const [selected,    setSelected]    = useState(null);
  const [searching,   setSearching]   = useState(false);
  const [submitting,  setSubmitting]  = useState(false);
  const [success,     setSuccess]     = useState(null);
  const [error,       setError]       = useState(null);
  const searchTimer = useRef(null);

  const [form, setForm] = useState({
    channel:          'poshmark',
    platform_order_id: '',
    buyer_name:       '',
    sale_price:       '',
    quantity:         1,
    manualSku:        '',
    address_text:     '',
  });

  // Search products as user types
  useEffect(() => {
    if (!search || search.length < 2) { setResults([]); return; }
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setSearching(true);
      api.get(`/products?search=${encodeURIComponent(search)}&limit=8`)
        .then(r => { setResults(r.data.products || []); setSearching(false); })
        .catch(() => setSearching(false));
    }, 300);
  }, [search]);

  function selectProduct(p) {
    setSelected(p);
    setSearch(p.title || p.sku);
    setResults([]);
  }

  function clearSelection() {
    setSelected(null);
    setSearch('');
    setResults([]);
  }

  async function handleSubmit() {
    const sku = selected?.sku || form.manualSku?.trim();
    if (!sku) { setError('Please select a product or enter a SKU'); return; }
    if (!form.buyer_name.trim() && !form.address_text.trim()) { setError('Buyer name or a pasted address is required'); return; }
    if (!form.sale_price || isNaN(parseFloat(form.sale_price))) { setError('Valid sale price is required'); return; }

    setSubmitting(true);
    setError(null);

    try {
      // Generate order ID if not provided
      const orderId = form.platform_order_id.trim() ||
        `${form.channel.toUpperCase()}-${Date.now()}`;

      const res = await api.post('/orders/manual', {
        platform_order_id: orderId,
        channel:           form.channel,
        sku:               sku,
        buyer_name:        form.buyer_name.trim(),
        sale_price:        parseFloat(form.sale_price),
        quantity:          parseInt(form.quantity) || 1,
        title:             selected?.title || search || sku,
        address_text:      form.address_text.trim() || undefined,
      });

      setSuccess({
        sku:      sku,
        title:    selected?.title || search || sku,
        location: selected?.location || selected?.sku_bin || sku.split('_')[0],
        channel:  form.channel,
        buyer:    res.data.order?.buyer_name || form.buyer_name,
        price:    form.sale_price,
        orderId,
      });

      // Reset form
      setSelected(null);
      setSearch('');
      setForm({ channel: 'poshmark', platform_order_id: '', buyer_name: '', sale_price: '', quantity: 1, manualSku: '', address_text: '' });
    } catch(e) {
      setError(e.response?.data?.error || e.message || 'Failed to create order');
    }
    setSubmitting(false);
  }

  return (
    <div className="page-content" style={{ maxWidth: 600 }}>
      <div className="page-header" style={{ marginBottom: 24 }}>
        <div className="page-title">✍️ Manual Order Entry</div>
        <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>
          For Poshmark, Mercari, and other channels
        </div>
      </div>

      {/* Success message */}
      {success && (
        <div style={{ background:'rgba(74,222,128,.08)', border:'1px solid rgba(74,222,128,.25)',
          borderRadius:14, padding:'20px', marginBottom:24 }}>
          <div style={{ fontSize:20, fontWeight:800, color:'#4ade80', marginBottom:12 }}>
            ✅ Order Created!
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, fontSize:13 }}>
            <div><span style={{ color:'#666' }}>SKU:</span> <span style={{ fontFamily:'monospace', color:'#6c63ff' }}>{success.sku}</span></div>
            <div><span style={{ color:'#666' }}>Channel:</span> {success.channel}</div>
            <div><span style={{ color:'#666' }}>Buyer:</span> {success.buyer}</div>
            <div><span style={{ color:'#666' }}>Price:</span> ${success.price}</div>
            <div><span style={{ color:'#666' }}>Order ID:</span> <span style={{ fontFamily:'monospace', fontSize:11 }}>{success.orderId}</span></div>
            <div><span style={{ color:'#666' }}>Status:</span> <span className="badge badge-warn">pending</span></div>
          </div>
          <div style={{ marginTop:16, background:'rgba(108,99,255,.1)', border:'1px solid rgba(108,99,255,.2)',
            borderRadius:10, padding:'12px 16px', display:'flex', alignItems:'center', gap:12 }}>
            <span style={{ fontSize:28 }}>📦</span>
            <div>
              <div style={{ fontSize:12, color:'#888' }}>Pick from bin</div>
              <div style={{ fontSize:22, fontWeight:900, color:'#a89fff', fontFamily:'monospace' }}>
                {success.location}
              </div>
            </div>
          </div>
          <button onClick={() => setSuccess(null)}
            style={{ marginTop:12, width:'100%', padding:'10px', background:'transparent',
              border:'1px solid #2a2d3e', borderRadius:10, color:'#888', cursor:'pointer', fontSize:13 }}>
            + Add Another Order
          </button>
        </div>
      )}

      {!success && (
        <div className="card" style={{ padding: 24 }}>

          {/* Product Search */}
          <div style={{ marginBottom: 20 }}>
            <label style={{ fontSize:12, color:'#888', fontWeight:600, display:'block', marginBottom:6 }}>
              SEARCH PRODUCT *
            </label>
            <div style={{ position:'relative' }}>
              <input
                type="text"
                value={search}
                onChange={e => { setSearch(e.target.value); if (selected) clearSelection(); }}
                placeholder="Search by SKU or title…"
                style={{ width:'100%', padding:'10px 14px', background:'#0f1117',
                  border:'1px solid ' + (selected ? '#4ade80' : '#2a2d3e'),
                  borderRadius:10, color:'#fff', fontSize:14, outline:'none', boxSizing:'border-box' }}
              />
              {searching && (
                <span style={{ position:'absolute', right:12, top:'50%', transform:'translateY(-50%)',
                  fontSize:12, color:'#666' }}>Searching…</span>
              )}
              {selected && (
                <button onClick={clearSelection}
                  style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)',
                    background:'none', border:'none', color:'#666', cursor:'pointer', fontSize:18 }}>✕</button>
              )}
            </div>

            {/* Search results dropdown */}
            {results.length > 0 && !selected && (
              <div style={{ background:'#1a1d2e', border:'1px solid #2a2d3e', borderRadius:10,
                marginTop:4, overflow:'hidden', boxShadow:'0 8px 24px rgba(0,0,0,.4)' }}>
                {results.map(p => (
                  <div key={p.sku} onClick={() => selectProduct(p)}
                    style={{ padding:'10px 14px', cursor:'pointer', borderBottom:'1px solid #2a2d3e',
                      display:'flex', alignItems:'center', gap:12 }}
                    onMouseEnter={e => e.currentTarget.style.background='#2a2d3e'}
                    onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                    {(() => { let src=null; try{src=JSON.parse(p.images||'[]')[0];}catch{} return src ? (
                      <img src={src} alt="" style={{ width:36, height:36, objectFit:'cover', borderRadius:6, flexShrink:0 }} />
                    ) : <div style={{ width:36, height:36, background:'#2a2d3e', borderRadius:6, flexShrink:0 }} />; })()}
                    <div style={{ minWidth:0, flex:1 }}>
                      <div style={{ fontSize:13, fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                        {p.title || p.sku}
                      </div>
                      <div style={{ display:'flex', gap:8, marginTop:2 }}>
                        <span style={{ fontSize:11, fontFamily:'monospace', color:'#6c63ff' }}>{p.sku}</span>
                        <span style={{ fontSize:11, color:'#888' }}>
                          📦 {p.location || p.sku_bin} · Qty: {p.quantity}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Selected product card */}
            {selected && (
              <div style={{ marginTop:8, background:'rgba(74,222,128,.05)', border:'1px solid rgba(74,222,128,.2)',
                borderRadius:10, padding:'10px 14px', display:'flex', alignItems:'center', gap:12 }}>
                {(() => { let src=null; try{src=JSON.parse(selected.images||'[]')[0];}catch{} return src ? (
                  <img src={src} alt="" style={{ width:48, height:48, objectFit:'cover', borderRadius:8, flexShrink:0 }} />
                ) : null; })()}
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:13, fontWeight:600, color:'#4ade80' }}>{selected.title}</div>
                  <div style={{ display:'flex', gap:8, marginTop:2 }}>
                    <span style={{ fontSize:11, fontFamily:'monospace', color:'#6c63ff' }}>{selected.sku}</span>
                    <span style={{ fontSize:11, color:'#888' }}>📦 {selected.location || selected.sku_bin} · {selected.quantity} in stock</span>
                  </div>
                </div>
              </div>
            )}

            {/* Manual SKU entry — when product not found in inventory */}
            {!selected && search.length >= 2 && results.length === 0 && !searching && (
              <div style={{ marginTop:8, background:'rgba(108,99,255,.05)', border:'1px solid rgba(108,99,255,.2)',
                borderRadius:10, padding:'12px 14px' }}>
                <div style={{ fontSize:12, color:'#888', marginBottom:8 }}>
                  Product not found in inventory. Enter a SKU to create the order anyway:
                </div>
                <input
                  value={form.manualSku}
                  onChange={e => setForm(f => ({...f, manualSku: e.target.value}))}
                  placeholder="Enter SKU (e.g. B12_123456789_26)"
                  style={{ width:'100%', padding:'10px 14px', background:'#0f1117',
                    border:'1px solid #2a2d3e', borderRadius:10, color:'#fff', fontSize:14, outline:'none', boxSizing:'border-box' }}
                />
              </div>
            )}
          </div>

          {/* Channel */}
          <div style={{ marginBottom:16 }}>
            <label style={{ fontSize:12, color:'#888', fontWeight:600, display:'block', marginBottom:6 }}>CHANNEL *</label>
            <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
              {CHANNELS.map(ch => (
                <button key={ch} onClick={() => setForm(f => ({...f, channel: ch}))}
                  style={{ padding:'7px 16px', borderRadius:20, border:'none', cursor:'pointer',
                    fontSize:13, fontWeight:600,
                    background: form.channel === ch ? '#6c63ff' : '#2a2d3e',
                    color: form.channel === ch ? '#fff' : '#888' }}>
                  {channelLabel(ch)}
                </button>
              ))}
            </div>
          </div>

          {/* Buyer Name */}
          <div style={{ marginBottom:16 }}>
            <label style={{ fontSize:12, color:'#888', fontWeight:600, display:'block', marginBottom:6 }}>BUYER NAME *</label>
            <input type="text" value={form.buyer_name}
              onChange={e => setForm(f => ({...f, buyer_name: e.target.value}))}
              placeholder="Buyer username or name"
              style={{ width:'100%', padding:'10px 14px', background:'#0f1117', border:'1px solid #2a2d3e',
                borderRadius:10, color:'#fff', fontSize:14, outline:'none', boxSizing:'border-box' }} />
          </div>

          {/* Ship-to address — pasted from the marketplace order page, same parser as the Labels page */}
          <div style={{ marginBottom:16 }}>
            <label style={{ fontSize:12, color:'#888', fontWeight:600, display:'block', marginBottom:6 }}>
              SHIP-TO ADDRESS <span style={{ color:'#555', fontWeight:400 }}>(optional — paste from the Mercari order page so the label is ready)</span>
            </label>
            <textarea rows={4} value={form.address_text}
              onChange={e => setForm(f => ({...f, address_text: e.target.value}))}
              placeholder={'Name\nStreet\nCity, ST 12345'}
              style={{ width:'100%', padding:'10px 14px', background:'#0f1117', border:'1px solid #2a2d3e',
                borderRadius:10, color:'#fff', fontSize:14, outline:'none', boxSizing:'border-box', fontFamily:'inherit', resize:'vertical' }} />
            <div style={{ fontSize:11, color:'#555', marginTop:4 }}>Buyer name is taken from the first line if left blank above.</div>
          </div>

          {/* Sale Price + Quantity */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
            <div>
              <label style={{ fontSize:12, color:'#888', fontWeight:600, display:'block', marginBottom:6 }}>SALE PRICE *</label>
              <input type="number" value={form.sale_price} min="0" step="0.01"
                onChange={e => setForm(f => ({...f, sale_price: e.target.value}))}
                placeholder="0.00"
                style={{ width:'100%', padding:'10px 14px', background:'#0f1117', border:'1px solid #2a2d3e',
                  borderRadius:10, color:'#fff', fontSize:14, outline:'none', boxSizing:'border-box' }} />
            </div>
            <div>
              <label style={{ fontSize:12, color:'#888', fontWeight:600, display:'block', marginBottom:6 }}>QUANTITY</label>
              <input type="number" value={form.quantity} min="1"
                onChange={e => setForm(f => ({...f, quantity: e.target.value}))}
                style={{ width:'100%', padding:'10px 14px', background:'#0f1117', border:'1px solid #2a2d3e',
                  borderRadius:10, color:'#fff', fontSize:14, outline:'none', boxSizing:'border-box' }} />
            </div>
          </div>

          {/* Order ID (optional) */}
          <div style={{ marginBottom:20 }}>
            <label style={{ fontSize:12, color:'#888', fontWeight:600, display:'block', marginBottom:6 }}>
              ORDER ID <span style={{ color:'#555', fontWeight:400 }}>(optional — auto-generated if blank)</span>
            </label>
            <input type="text" value={form.platform_order_id}
              onChange={e => setForm(f => ({...f, platform_order_id: e.target.value}))}
              placeholder="e.g. 69c3f80c57a99058bd52b6ad"
              style={{ width:'100%', padding:'10px 14px', background:'#0f1117', border:'1px solid #2a2d3e',
                borderRadius:10, color:'#fff', fontSize:14, outline:'none', boxSizing:'border-box' }} />
          </div>

          {/* Error */}
          {error && (
            <div style={{ background:'rgba(239,68,68,.1)', border:'1px solid rgba(239,68,68,.3)',
              borderRadius:10, padding:'10px 14px', marginBottom:16, color:'#ef4444', fontSize:13 }}>
              ⚠️ {error}
            </div>
          )}

          {/* Submit */}
          <button onClick={handleSubmit} disabled={submitting || (!selected && !form.manualSku.trim())}
            style={{ width:'100%', padding:'14px', borderRadius:12, border:'none',
              cursor: (selected || form.manualSku.trim()) ? 'pointer' : 'not-allowed',
              background: (selected || form.manualSku.trim()) ? 'linear-gradient(135deg,#6c63ff,#5b54e8)' : '#2a2d3e',
              color: (selected || form.manualSku.trim()) ? '#fff' : '#555', fontSize:16, fontWeight:700,
              boxShadow: (selected || form.manualSku.trim()) ? '0 4px 16px rgba(108,99,255,.3)' : 'none' }}>
            {submitting ? '⏳ Creating Order…' : selected ? '✅ Create Order & Update Inventory' : '✅ Create Order'}
          </button>

          <div style={{ fontSize:11, color:'#555', textAlign:'center', marginTop:10 }}>
            This will create a pending order and reduce inventory by the quantity
          </div>
        </div>
      )}
    </div>
  );
}
